import { differenceInMinutes } from "date-fns";
import { ArrowRightIcon, Clock, ExternalLinkIcon, Plane, StepForwardIcon } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { InvoluntaryBadge, TicketStatusBadge } from "../passengers/coupon-history-detail";
import {
  type ComputedItineraryEntry,
  calculateDuration,
  displayDelta,
  formatDate,
  formatTime,
} from "./init-incident-logic";

/**
 * Presentational card for one computed-itinerary flight (original or actual).
 * Port of the flight blocks of InitIncident.vue.
 */

function AirportCode({ code, city }: { code?: string | null; city?: string | null }) {
  if (!city) {
    return <div className="text-lg font-bold text-slate-800">{code}</div>;
  }
  return (
    <Tooltip>
      <TooltipTrigger className="cursor-help text-lg font-bold text-slate-800">{code}</TooltipTrigger>
      <TooltipContent>{city}</TooltipContent>
    </Tooltip>
  );
}

interface ComputedFlightCardProps {
  entry: ComputedItineraryEntry;
  type: "original" | "actual";
  isLast: boolean;
  /** Selection mode (original column only): shows the checkbox — mirrors flightSelectionType (Vue). */
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export function ComputedFlightCard({
  entry,
  type,
  isLast,
  selectable = false,
  selected = false,
  onSelect,
}: ComputedFlightCardProps) {
  const { flight, ...ticketLeg } = entry;
  const isCancelled = flight.monitoringStatus === "CANCELLED";

  // Original column: mirrors the ">30 min" thresholds of InitIncident.vue;
  // actual column keeps the Vue ">50 min" arrival threshold. (The Vue source used
  // date-fns/fp whose arguments are data-last — hence actual − scheduled here.)
  const departureDelayMinutes = flight.actualDepartureUTC
    ? differenceInMinutes(
        new Date(String(flight.actualDepartureUTC)),
        new Date(String(flight.scheduledDepartureUTC)),
      )
    : null;
  const arrivalDelayMinutes = flight.inGateArrivalUTC
    ? differenceInMinutes(
        new Date(String(flight.inGateArrivalUTC)),
        new Date(String(flight.scheduledArrivalUTC)),
      )
    : null;

  const departureDisrupted =
    type === "original" && departureDelayMinutes !== null && departureDelayMinutes > 30;
  const arrivalThreshold = type === "original" ? 30 : 50;
  const arrivalDisrupted =
    isCancelled || (arrivalDelayMinutes !== null && arrivalDelayMinutes > arrivalThreshold);

  const hasLateArrival = arrivalDelayMinutes !== null && arrivalDelayMinutes > 50;

  const departureTimezone = flight.departureAirport?.timezone ?? "UTC";
  const arrivalTimezone = flight.arrivalAirport?.timezone ?? "UTC";

  const cancellationDateTitle = ticketLeg.segmentInfo?.cancellationDate
    ? new Date(String(ticketLeg.segmentInfo.cancellationDate)).toLocaleDateString()
    : "";

  return (
    <div className="relative">
      <div className="p-6 transition-colors duration-200">
        {/* Flight Route */}
        <div className="flex items-center pb-4 text-left text-base font-bold leading-8 text-blue-700">
          {selectable ? (
            <div className="mr-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onSelect?.()}
                className="h-6 w-6 appearance-none rounded-sm border-2 border-pink-500 outline outline-2 outline-offset-2 outline-blue-500 checked:border-pink-500 checked:bg-blue-500 checked:outline-blue-500 focus:outline-4 focus:outline-offset-2 focus:outline-blue-600"
              />
            </div>
          ) : null}
          <h2 className="inline-flex items-center">
            <a
              className="inline-flex items-center"
              href={`https://www.flightradar24.com/data/flights/${flight.flightIdentifier?.toLowerCase()}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <p>
                Vol <span className="underline">{flight.flightIdentifier}</span>
              </p>
              <ExternalLinkIcon className="mx-2 h-4 w-4 text-gray-500" />
            </a>
            <div className="mx-2">
              <TicketStatusBadge status={ticketLeg.status} statusDescription={ticketLeg.statusDescription} />
            </div>
            {type === "original" && ticketLeg.segmentInfo?.cancellationType === "SEGMENT" ? (
              <span
                title={cancellationDateTitle}
                className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
              >
                <svg className="size-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
                  <circle cx="3" cy="3" r="3" />
                </svg>
                HX
              </span>
            ) : type === "original" && ticketLeg.segmentInfo?.cancellationType === "RECORD" ? (
              <span
                title={cancellationDateTitle}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-zinc-500 px-2 py-1 text-xs font-medium text-gray-100 ring-1 ring-inset ring-zinc-400"
              >
                <svg className="size-1.5 fill-white" viewBox="0 0 6 6" aria-hidden="true">
                  <circle cx="3" cy="3" r="3" />
                </svg>
                Cancel
              </span>
            ) : null}
            {type === "actual" ? (
              <div>
                <InvoluntaryBadge involuntaryIndicator={ticketLeg.lastInvoluntaryIndicator} />
              </div>
            ) : null}
            {type === "original" ? (
              <div className="absolute right-0 top-5 z-50 text-slate-500">
                <ArrowRightIcon className="h-12 w-12" />
              </div>
            ) : null}
          </h2>
        </div>

        {/* Route */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <AirportCode code={ticketLeg.from} city={flight.departureAirport?.city} />
              <div className="flex items-center gap-2">
                <div className="h-px w-12 bg-slate-300" />
                <Plane className="h-4 w-4 rotate-45 transform text-slate-400" />
                <div className="h-px w-12 bg-slate-300" />
              </div>
              <AirportCode code={ticketLeg.to} city={flight.arrivalAirport?.city} />
            </div>
          </div>
        </div>

        {/* Flight Times */}
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Départ</div>
            {departureDisrupted ? (
              <div className="inline-flex items-center text-lg font-semibold tracking-tight text-slate-800">
                <span className="line-through">
                  {formatTime(flight.scheduledDepartureUTC, departureTimezone)}
                </span>
                <StepForwardIcon className="mx-3 h-5 w-5 text-slate-500" />
                {flight.actualDepartureUTC ? (
                  <span className="text-amber-600">
                    {formatTime(flight.actualDepartureUTC, departureTimezone)}
                  </span>
                ) : !flight.monitoringStatus ? (
                  <span className="lowercase tracking-tighter text-stone-800">Aucun donnée</span>
                ) : null}
              </div>
            ) : (
              <div className="text-lg font-semibold tracking-tight text-slate-800">
                {formatTime(flight.scheduledDepartureUTC, departureTimezone)}
              </div>
            )}
            <div className="text-sm text-slate-500">
              {formatDate(flight.scheduledDepartureUTC, departureTimezone)}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Arrivée</div>
            {arrivalDisrupted ? (
              <div className="inline-flex items-center text-lg font-semibold tracking-tight text-slate-800">
                <span className="line-through">
                  {formatTime(flight.scheduledArrivalUTC, arrivalTimezone)}
                </span>
                <StepForwardIcon className="mx-3 h-5 w-5 text-slate-500" />
                {flight.inGateArrivalUTC ? (
                  <span className="text-amber-600">
                    {formatTime(flight.inGateArrivalUTC, arrivalTimezone)}
                  </span>
                ) : isCancelled ? (
                  <span className="lowercase tracking-tighter text-red-800">ANNULÉ</span>
                ) : null}
              </div>
            ) : (
              <div className="inline-flex items-center text-lg font-semibold tracking-tight text-slate-800">
                {formatTime(flight.scheduledArrivalUTC, arrivalTimezone)}
              </div>
            )}
            <div className="text-sm text-slate-500">
              {formatDate(flight.scheduledArrivalUTC, arrivalTimezone)}
            </div>
          </div>
        </div>

        {/* Flight Duration */}
        <div className="mt-3 grid grid-cols-2 gap-6 text-xs text-slate-500">
          <div>Durée: {calculateDuration(flight.scheduledDepartureUTC, flight.scheduledArrivalUTC)}</div>
          {hasLateArrival ? (
            <div className="italic text-amber-600">
              Retard de {calculateDuration(flight.scheduledArrivalUTC, flight.inGateArrivalUTC)}
            </div>
          ) : null}
        </div>

        {/* Delta (actual column) */}
        {type === "actual" && ticketLeg.delta && (ticketLeg.delta.arrival || ticketLeg.delta.departure) ? (
          <div className="mt-3 text-xs text-red-800">
            {ticketLeg.delta.departure < -60 ? (
              <span>
                Delta départ:{" "}
                <span className="font-semibold tracking-tight text-indigo-800">
                  {displayDelta(ticketLeg.delta.departure)}
                </span>
              </span>
            ) : (
              <span>
                Delta arrivée:{" "}
                <span className="font-semibold tracking-tight text-red-800">
                  {displayDelta(ticketLeg.delta.arrival)}
                </span>
              </span>
            )}
          </div>
        ) : null}

        {/* Operating airline */}
        {flight.operatingAirline ? (
          <div className="mt-3 text-xs text-indigo-500">
            Opéré par: <span className="font-semibold">{flight.operatingAirline?.name}</span>
          </div>
        ) : null}
      </div>

      {/* Connection Line */}
      {!isLast ? (
        <div className="flex justify-center bg-slate-50 py-2">
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1">
            <Clock className="h-3 w-3 text-slate-400" />
            <span className="text-xs text-slate-500">Correspondance</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
